import { NgClass } from '@angular/common';
import { Component, computed, effect, input, output, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { ManualDeliverableStatusComponent } from '@cta-analytics-details/cta-analytics-details-deliverable-card/manual-deliverable-status/manual-deliverable-status.component';
import { IManualDeliverableStatusConfig } from '@cta-analytics-details/cta-analytics-details-deliverable-card/manual-deliverable-status/manual-devlierable-status.interface';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { take } from 'rxjs';

@Component({
  imports: [
    TranslateModule,
    MatButtonModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    SocialBadgeComponent,
    MatTooltipModule,
    TextClampTooltipDirective,
    NgClass,
    IconComponent,
    ManualDeliverableStatusComponent,
    MatErrorComponent,
  ],
  selector: 'app-editable-title',
  styleUrl: './editable-title.component.scss',
  templateUrl: './editable-title.component.html',
})
export class EditableTitleComponent {
  public readonly title = input<string | null>(null);
  public readonly subtitle = input<string | null>(null);
  public readonly icon = input<string | null>('icons/delivery-parcel.svg');
  public readonly iconColor = input<string | null>('var(--shikenso-purple-base)');
  public readonly iconSize = input<number>(16);
  public readonly socialIcon = input<EPlatformFeature | null>(null);
  public readonly control = input<FormControl<string | null>>();
  public readonly hideSubtitleWhenEditing = input<boolean>(true);
  public readonly disabled = input<boolean>(false);
  public readonly isViewMode = input<boolean>(false);
  public readonly maxWidth = input<number | undefined>(undefined);
  public readonly fontClasses = input<string | null>(null);
  public readonly prioritizeTitle = input<boolean>(false);
  public readonly readonly = input<boolean>(false);
  public readonly infoTooltip = input<string | null>(null);
  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly showRequiredBadge = input<boolean>(false);

  public readonly manualDeliverableStatusConfig = input<IManualDeliverableStatusConfig | TNil>(null);
  public readonly hideManualDeliverableStatusTooltip = input<boolean>(false);

  /** When true, entering edit mode is deferred: clicking the edit icon emits `editModeRequested`
   *  instead of switching to edit mode immediately, so a consumer can gate it (e.g. behind a
   *  confirmation flow) and call `enterEditMode()` once allowed. */
  public readonly gateEditMode = input<boolean>(false);
  public readonly editModeRequested = output<void>();
  public readonly editModeChanged = output<boolean>();

  public readonly isEditMode = signal<boolean>(false);

  public readonly analyticsMode = computed(() => {
    return !!this.manualDeliverableStatusConfig();
  });

  private originalTitle: string | null = null;

  constructor() {
    effect(() => {
      if (this.originalTitle) {
        return;
      }

      const control = this.control();
      this.originalTitle = control?.value ?? this.title();

      control?.valueChanges.pipe(take(1)).subscribe(() => {
        control?.markAsDirty();
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });
    });
  }

  public onEditModeChange(event: Event, value: boolean): void {
    event?.stopPropagation();

    if (this.readonly()) {
      return;
    }

    if (value && this.gateEditMode()) {
      this.editModeRequested.emit();

      return;
    }

    this.isEditMode.set(value);
    this.editModeChanged.emit(value);
  }

  /** Lets a consumer actually enter edit mode once it has decided to allow a deferred request. */
  public enterEditMode(): void {
    this.isEditMode.set(true);
    this.editModeChanged.emit(true);
  }

  /** Leaves edit mode without touching the control's value — used when a consumer just wants the
   *  title to stop showing the input (e.g. its containing panel collapsed), without discarding
   *  whatever the user had typed. */
  public exitEditMode(): void {
    if (!this.isEditMode()) {
      return;
    }

    this.isEditMode.set(false);
    this.editModeChanged.emit(false);
  }

  /** Re-baselines what a later `discardEditMode()`/Cancel reverts to, without touching edit mode
   *  or the control's live value — used when a consumer has just re-hydrated the control from a
   *  new authoritative value (e.g. a save response) elsewhere, so Cancel reverts to that instead
   *  of the stale pre-save title. */
  public syncOriginalTitle(value: string | null): void {
    this.originalTitle = value;
  }

  /** Leaves edit mode AND reverts the control back to its last confirmed value — used when a
   *  consumer is discarding unsaved changes on behalf of the user. */
  public discardEditMode(): void {
    const control = this.control();

    this.isEditMode.set(false);
    this.editModeChanged.emit(false);

    if (control && control.value !== this.originalTitle) {
      control.setValue(this.originalTitle);
    }
  }

  public onFormSave(event: Event): void {
    event?.stopPropagation();

    const control = this.control();

    if (!control?.valid) {
      return;
    }

    this.isEditMode.set(false);
    this.editModeChanged.emit(false);

    this.originalTitle = control.value;
  }

  public onFormCancel(event: Event): void {
    event?.stopPropagation();
    this.discardEditMode();
  }
}
